import { useCallback, useEffect, useRef } from 'react';

const REFRESH_INTERVAL_MS = 30 * 60 * 1000;  // normal gap between timed refreshes (30 minutes)
const CATCH_UP_MIN_GAP_MS = 60 * 1000;        // no catch-up on focus / visible if a refresh succeeded this recently
const BACKOFF_AFTER       = 3;                // consecutive failures before the interval starts doubling
const MAX_BACKOFF_MS      = 2 * 60 * 60 * 1000; // backoff cap (2 hours); never below the normal interval

// True when two fetched payloads are the same, so callers can skip setState and the re-render.
export function isSameData(a, b) {
  try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; }
}

// Calls refreshFn every intervalMs (30 minutes) while enabled, the tab is visible and paused is
// false. The timer restarts after every refresh (timed, catch-up or manual), so the next timed
// refresh is always a full interval after the most recent one. Returning to the tab refreshes
// right away unless a refresh succeeded in the last minute.
//
// refreshFn(shouldApply) must fetch and return a promise. A rejection counts as a failure
// (silent to the user). Call shouldApply() after the fetch and skip setState when it returns
// false: it is false if the user paused mid-fetch, the refresh function changed (new filters),
// or the component unmounted, so a slow response never overwrites newer state.
//
// refreshNow() is for the manual Refresh button. It ignores paused and rejects on failure so
// the caller can show its own message.
export default function useAutoRefresh(refreshFn, { intervalMs = REFRESH_INTERVAL_MS, paused = false, enabled = true, onRefreshed } = {}) {
  const fnRef          = useRef(refreshFn);
  const onRefreshedRef = useRef(onRefreshed);
  const pausedRef      = useRef(paused);
  const wasPausedRef   = useRef(paused);
  const epochRef       = useRef(0);
  const inFlightRef    = useRef(false);
  const failuresRef    = useRef(0);
  const lastRunRef     = useRef(0);   // when the last refresh succeeded
  const restartRef     = useRef(null); // restarts the timer; set while the schedule effect is active
  const mountedRef     = useRef(true);

  useEffect(() => { fnRef.current = refreshFn; onRefreshedRef.current = onRefreshed; });

  useEffect(() => () => { mountedRef.current = false; }, []);

  // A pause starting or ending invalidates any fetch that began before it.
  useEffect(() => {
    pausedRef.current = paused;
    epochRef.current += 1;
  }, [paused]);

  const run = useCallback(async (manual) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const fn = fnRef.current;
    const epoch = epochRef.current;
    const shouldApply = () => mountedRef.current && fnRef.current === fn &&
      (manual || (!pausedRef.current && epochRef.current === epoch));
    try {
      await fn(shouldApply);
      failuresRef.current = 0;
      lastRunRef.current = Date.now();
      if (mountedRef.current) onRefreshedRef.current?.(new Date());
    } catch (err) {
      failuresRef.current += 1;
      throw err;
    } finally {
      inFlightRef.current = false;
      if (restartRef.current) restartRef.current();
    }
  }, []);

  const refreshNow = useCallback(() => run(true), [run]);

  useEffect(() => {
    if (!enabled || paused) {
      wasPausedRef.current = paused;
      return undefined;
    }
    const resumed = wasPausedRef.current;
    wasPausedRef.current = false;

    let cancelled = false;
    let timer = null;

    const delay = () => {
      const n = failuresRef.current;
      return n >= BACKOFF_AFTER
        ? Math.min(intervalMs * 2 ** (n - BACKOFF_AFTER + 1), Math.max(MAX_BACKOFF_MS, intervalMs))
        : intervalMs;
    };
    const schedule = () => {
      clearTimeout(timer);
      if (!cancelled) timer = setTimeout(tick, delay());
    };
    restartRef.current = schedule;
    async function tick() {
      clearTimeout(timer);
      if (!document.hidden) {
        try { await run(false); } catch { /* silent: keep the data on screen */ }
      }
      schedule();
    }
    const catchUp = () => {
      if (document.hidden || Date.now() - lastRunRef.current < CATCH_UP_MIN_GAP_MS) return;
      tick();
    };
    const onVisibility = () => { if (!document.hidden) catchUp(); };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', catchUp);

    if (resumed) tick(); else schedule();

    return () => {
      cancelled = true;
      restartRef.current = null;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', catchUp);
    };
  }, [enabled, paused, intervalMs, run]);

  return { refreshNow };
}
